import { designTokens } from "@itandi/itandi-bb-ui";
import { Accordion } from "@itandi/itandi-bb-ui/Accordion";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ComponentProps } from "react";
import React from "react";

import { ConstructionAddForm } from "~/components/common/constructions/adding/ConstructionAddForm";
import { ConstructionAddModal } from "~/components/common/constructions/adding/ConstructionAddModal";
import { ConstructionRequestFooter } from "~/components/common/constructions/ConstructionRequestFooter";
import { ConstructionList } from "~/components/common/constructions/list/ConstructionList";
import { ConstructionDownloadButtons } from "~/components/common/constructions/top/ConstructionDownloadButtons";
import { ConstructionTop } from "~/components/common/constructions/top/ConstructionTop";
import { VendorDealLayout } from "~/components/common/layouts/deals/VendorDealLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import Loading from "~/components/common/parts/loading/Loading";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useDealConstructionItems } from "~/hooks/vendors/deals/useDealConstructionItems";
import { useDealEstimationHeaders } from "~/hooks/vendors/deals/useDealEstimationHeaders";
import { useDealEstimationHistories } from "~/hooks/vendors/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/vendors/deals/useDealEstimations";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { NskResponse } from "~/models/Api";
import { estimationHeaderStatus } from "~/models/Deal";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import { checkConstructionAddDisabled } from "~/models/DealConstruction";
import { estimationRequestSide } from "~/models/DealEstimationHeader";
import {
	estimationConstructionContext,
	historyConstructionContext
} from "~/models/DealEstimationHistory";
import {
	getOriginDealVendor,
	getSubDealVendor,
	isSubVendor
} from "~/models/DealVendorCompany";
import {
	canEstimationRequestStatus,
	IN_CONSTRUCTION,
	IN_ESTIMATION,
	SEND_ESTIMATE
} from "~/models/Status";
import { PATH as DEALS_PATH } from "~/pages/vendors/deals";
import { PATH } from "~/pages/vendors/deals/[id]";
import { PATH as ESTIMATIONS_PATH } from "~/pages/vendors/deals/[id]/estimations";
import { PATH as ACCOUNT_PATH } from "~/pages/vendors/settings/accounts";
import { isSuccess } from "~/utils/api/api_handler";
import { updateSeq as estimationUpdateSeq } from "~/utils/api/vendors/deals/DealEstimation";
import { create } from "~/utils/api/vendors/deals/DealEstimationComplete";
import { getDownloadPath } from "~/utils/api/vendors/deals/DealEstimationFile";
import {
	create as createHeader,
	destroy,
	update,
	updateRequest
} from "~/utils/api/vendors/deals/DealEstimationHeader";
import {
	destroy as destroyEstimationHistory,
	updateSeq as historyUpdateSeq,
	create as postDealEstimationHistory,
	updateTypeDelete
} from "~/utils/api/vendors/deals/DealEstimationHistory";
import { getDownloadPath as getHistoryDownloadPath } from "~/utils/api/vendors/deals/DealEstimationHistoryFile";
import { NoteEditModal } from "./NoteEditModal";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const { notify } = useNotification();
	const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
	const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const {
		isOpen: isOpenCreate,
		handleOpen: handleOpenCreate,
		handleClose: handleCloseCreate
	} = useOpenHandler();
	const {
		isOpen: showConfirm,
		handleOpen: handleOpenConfirm,
		handleClose: handleCloseConfirm
	} = useOpenHandler();
	const [isUpdateNoteModalOpen, setIsUpdateNoteModalOpen] =
		React.useState(false);
	const { data: session } = useSessions().index();
	const router = useRouter();
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: dealConstructionItems } = useDealConstructionItems(id);
	const { data: estimationContext } = useDealEstimations().index(id);
	const {
		data: dealEstimationHistoryContext,
		mutate: mutateEstimationHistoryContext
	} = useDealEstimationHistories().index(id);
	const { data: dealEstimationHeaders, mutate: mutateHeader } =
		useDealEstimationHeaders().index(id);
	const requestHeader =
		dealEstimationHeaders?.find((header) => header.status === "request") ??
		null;
	const { data: requestHistoryContext, isLoading } =
		useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
	const dealEstimationHeaderId =
		dealEstimationHistoryContext?.dealEstimationHeaderId ?? null;
	const vendorCompanyId = session?.vendorCompany.id ?? null;
	React.useEffect(() => {
		if (deal === null) return;
		if (!estimationRequestSide(deal, "vendor")) {
			router.replace(ESTIMATIONS_PATH(deal.id)).then();
		}
	}, [deal, router]);
	if (
		deal === null ||
		isLoading ||
		session === null ||
		estimationContext === null ||
		dealEstimationHistoryContext === null ||
		dealEstimationHeaders === null ||
		dealConstructionItems === null ||
		vendorCompanyId === null
	) {
		return <Loading isLoading />;
	}
	const showRequestFooter =
		canEstimationRequestStatus(deal.statusId) &&
		(dealEstimationHistoryContext?.dealConstructions.length !== 0 ||
			requestHeader !== null);
	const constructionAddDisabled = checkConstructionAddDisabled(
		"estimation",
		requestHeader,
		deal
	);
	const subVendor = getSubDealVendor(deal);
	const estimationNote = (): string => {
		if (
			// dealEstimationHeadersは確定している見積りを返すので、length === 0 はdraft以外の見積りが存在しないことを意味する
			dealEstimationHeaders.length === 0 &&
			// 備考に差分がない時は施工会社のテンプレートを表示する
			!dealEstimationHistoryContext.noteModified
		) {
			// 見積り備考のテンプレートは実施会社を優先する
			return subVendor
				? (subVendor.estimationNote ?? "")
				: (getOriginDealVendor(deal)?.estimationNote ?? "");
		}
		// ドラフト・リクエストの見積りはそれぞれuniqueなので、どちらかが存在していれば表示する。
		// どちらもなければ、確定している見積りの備考を表示する。
		return (
			dealEstimationHistoryContext.note ??
			requestHeader?.note ??
			estimationContext.note ??
			""
		);
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "見積り作成" }
	] as const satisfies ComponentProps<typeof VendorDealLayout>["items"];
	const handleClickPreview = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};
	const buildHistoryDownloadPath = (): string => {
		// 申請前(draftのheader)
		if (dealEstimationHeaderId) {
			return getHistoryDownloadPath(deal.id, dealEstimationHeaderId);
		}
		// 申請中(requestのheader)
		if (requestHeader) {
			return getHistoryDownloadPath(deal.id, requestHeader.id);
		}
		// どちらもないとき
		return "";
	};
	const handleClickHistoryPreview = (): void => {
		const path = buildHistoryDownloadPath();
		window.open(path);
	};
	const handleClickShowAddModal = (): void => {
		setShowAddModal(true);
	};
	const handleMutateEstimationHistories = async (): Promise<void> => {
		await mutateEstimationHistoryContext?.();
		await mutateDeal?.();
	};
	const handleMutateRequestHeader = async (): Promise<void> => {
		await mutateHeader?.();
	};
	const handleCreate = async (
		submitValue: DealConstructionEdit
	): Promise<NskResponse> =>
		postDealEstimationHistory(submitValue, deal.id, null, null);
	const handleUpdate = async (
		submitValue: DealConstructionEdit,
		estimationId: number | null,
		historyId: number | null
	): Promise<NskResponse> => {
		if (estimationId !== null) {
			return postDealEstimationHistory(
				submitValue,
				deal.id,
				estimationId,
				null
			);
		}
		if (historyId === null) {
			throw new Error("historyId cannot be null");
		}
		return postDealEstimationHistory(submitValue, deal.id, null, historyId);
	};
	const handleDestroy = async (
		dealConstruction: DealConstruction
	): Promise<NskResponse> => {
		if (dealConstruction.type === "DealBilling") {
			throw new Error("deleteEstimation.type cannot be DealBilling");
		}
		if (dealConstruction.type === "DealEstimationHistory") {
			if (dealConstruction.operationType === "add") {
				// そのとき新規追加したものはデータを消す
				return destroyEstimationHistory(deal.id, dealConstruction.id);
			}
			if (dealConstruction.dealEstimation === null) {
				throw new Error("dealEstimation cannot be null");
			}
			// すでに承認されていたものは、申請を通じて削除する
			return updateTypeDelete(deal.id, dealConstruction.dealEstimation.id);
		}
		// すでに承認されていたものは、申請を通じて削除する
		return updateTypeDelete(deal.id, dealConstruction.id);
	};
	const handleUpdateSeq = async (
		newEstimations: DealConstruction[]
	): Promise<void> => {
		if (newEstimations.length === 0) {
			throw new Error("newEstimations.length cannot be 0");
		}
		if (newEstimations[0].type === "DealEstimation") {
			await estimationUpdateSeq(newEstimations, deal.id);
			return;
		}
		await historyUpdateSeq(newEstimations, deal.id);
	};
	const handleSubmitComplete = async (): Promise<void> => {
		const result = await create(deal.id);
		if (isSuccess(result)) {
			await router.push(PATH(deal.id));
			notify({ content: "完了しました。", variant: "success" });
			return;
		}
		notify({
			content: `エラーが発生しました。${result.message}`,
			variant: "error"
		});
	};
	const handleUpdateRequestStatus = async (): Promise<NskResponse> => {
		if (dealEstimationHeaderId === null) {
			throw new Error("dealEstimationHeaderId cannot be null");
		}
		return updateRequest(deal.id, dealEstimationHeaderId);
	};
	const handleUpdateCancelStatus = async (): Promise<NskResponse> => {
		if (requestHeader === null) {
			throw new Error("requestHeader cannot be null");
		}
		return update(deal.id, requestHeader.id, "cancel");
	};
	const handleClickCreateHeader = async (): Promise<void> => {
		const result = await createHeader(deal.id);
		if (isSuccess(result)) {
			await mutateEstimationHistoryContext?.();
			await mutateDeal?.();
			notify({ content: "見積りを複製しました。", variant: "success" });
			handleCloseCreate();
			return;
		}
		handleCloseCreate();
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	const handleClickDestroyHeader = async (): Promise<void> => {
		if (dealEstimationHeaderId === null) {
			throw new Error("dealEstimationHeaderId cannot be null");
		}
		const result = await destroy(deal.id, dealEstimationHeaderId);
		if (isSuccess(result)) {
			await mutateEstimationHistoryContext?.();
			await mutateDeal?.();
			notify({
				content: "編集中の見積りを元に戻しました。",
				variant: "success"
			});
			handleClose();
			return;
		}
		handleClose();
		notify({
			content: result.message ?? "エラーが発生しました。",
			variant: "error"
		});
	};
	if (!windowLoaded) return null;
	const estimationStatus = estimationHeaderStatus(deal);

	return (
		<VendorLayoutV2>
			<VendorDealLayout deal={deal} enableChat items={items}>
				<Flex
					justifyContent="space-between"
					pt={isSp ? designTokens["spacing/m"] : undefined}
					px={isSp ? designTokens["spacing/m"] : undefined}
				>
					<Flex alignItems="center" gap={designTokens["spacing/s"]}>
						<h1 className={Styles.EstimateTitle}>見積り作成</h1>
						{estimationStatus.label !== "" &&
							estimationStatus.variant !== null && (
								<PresentationChip variant={estimationStatus.variant}>
									{estimationStatus.label}
								</PresentationChip>
							)}
					</Flex>
					{!isSp ? (
						<Flex gap={designTokens["spacing/xs"]}>
							<ConstructionDownloadButtons
								constructionContext={estimationContext}
								dealEstimationDraftHeader={dealEstimationHistoryContext}
								dealEstimationHistoryContext={historyConstructionContext(
									requestHistoryContext,
									dealEstimationHistoryContext
								)}
								handleClickEstimationHistoryPreview={
									buildHistoryDownloadPath() === ""
										? null
										: handleClickHistoryPreview
								}
								handleClickEstimationPreview={handleClickPreview}
								ownManagement={false}
							/>
						</Flex>
					) : null}
				</Flex>
				<Flex direction="column" gap={designTokens["spacing/s"]}>
					{estimationHeaderStatus(deal).description !== "" &&
						deal.statusId !== IN_ESTIMATION && (
							<Grid paddingX={isSp ? 16 : 0} width="fit-content">
								<Alertv2 variant="warning">
									<Paragraph>
										{estimationHeaderStatus(deal).description}
									</Paragraph>
								</Alertv2>
							</Grid>
						)}
					{estimationHeaderStatus(deal).label === "" &&
						deal.statusId === SEND_ESTIMATE && (
							<Grid paddingX={isSp ? 16 : 0} width="fit-content">
								<Alertv2 variant="info">
									<Paragraph>
										管理会社が見積りを確認しています。施工に関しては、工事完了待ちステータスに進行されるまでお待ちください。
									</Paragraph>
								</Alertv2>
							</Grid>
						)}
					{estimationHeaderStatus(deal).label === "" &&
						deal.statusId === IN_CONSTRUCTION && (
							<Grid paddingX={isSp ? 16 : 0} width="fit-content">
								<Alertv2 variant="info">
									<Paragraph>
										発注対象となっている施工項目です。施工手続きをお願いします。
									</Paragraph>
								</Alertv2>
							</Grid>
						)}
					{deal.statusId === IN_ESTIMATION && (
						<Grid paddingX={isSp ? 16 : 0} width="fit-content">
							<Alertv2 variant="warning">
								<Paragraph>
									初めて見積確認中ステータスに移行する場合、変更前や削除した見積情報の編集履歴を管理会社は確認できません。
								</Paragraph>
							</Alertv2>
						</Grid>
					)}
				</Flex>
				<ConstructionTop
					constructionContext={estimationContext}
					dealEstimationDraftHeader={dealEstimationHistoryContext}
					dealEstimationHistoryContext={historyConstructionContext(
						requestHistoryContext,
						dealEstimationHistoryContext
					)}
					estimationAddDisabled={requestHeader !== null}
					handleClickCreateHeader={
						deal.latestEstimationRejected ? handleOpenCreate : null
					}
					handleClickDestroyHeader={handleOpen}
					handleClickShowAddModal={handleClickShowAddModal}
					readonly={false}
				/>
				{isSp ? (
					<Flex
						direction="column"
						gap={designTokens["spacing/s"]}
						marginBottom={designTokens["spacing/l"]}
						marginTop={designTokens["spacing/s"]}
						marginX={designTokens["spacing/l"]}
					>
						<Divider />
						<ConstructionDownloadButtons
							constructionContext={estimationContext}
							dealEstimationDraftHeader={dealEstimationHistoryContext}
							dealEstimationHistoryContext={historyConstructionContext(
								requestHistoryContext,
								dealEstimationHistoryContext
							)}
							handleClickEstimationHistoryPreview={
								buildHistoryDownloadPath() === ""
									? null
									: handleClickHistoryPreview
							}
							handleClickEstimationPreview={handleClickPreview}
							ownManagement={false}
							width="fill"
						/>
					</Flex>
				) : null}
				<ConfirmModal
					confirmType="save"
					handleAccept={handleClickCreateHeader}
					handleCancel={handleCloseCreate}
					isOpen={isOpenCreate}
				>
					棄却された見積りを元に見積りを複製しますか？
				</ConfirmModal>
				<ConfirmModal
					confirmType="save"
					handleAccept={handleClickDestroyHeader}
					handleCancel={handleClose}
					isOpen={isOpen}
				>
					現在編集中の見積りを元に戻しますか？
					<Alertv2 marginTop={designTokens["spacing/xs"]} variant="warning">
						<Paragraph>既に反映されている見積りの状態に戻ります。</Paragraph>
					</Alertv2>
				</ConfirmModal>
				<div className={Styles.Container}>
					<div className={Styles.EstimateAddForm}>
						<ConstructionAddForm
							dealConstructionItems={dealConstructionItems}
							disabled={constructionAddDisabled}
							docType="estimation"
							handleCreate={handleCreate}
							mutateConstructions={handleMutateEstimationHistories}
						/>
					</div>
					<div className={Styles.EstimateList}>
						<ConstructionList
							companyType="vendor"
							constructionContext={estimationConstructionContext(
								requestHistoryContext,
								dealEstimationHistoryContext,
								estimationContext
							)}
							deal={deal}
							dealConstructionItems={dealConstructionItems}
							docType="estimation"
							handleDestroy={handleDestroy}
							handleMutateConstructions={handleMutateEstimationHistories}
							handleUpdate={handleUpdate}
							handleUpdateSeq={requestHeader === null ? handleUpdateSeq : null}
							readonly={false}
							requestHeader={requestHeader}
							showEstimationConfirmedButton={showRequestFooter}
							showHighlight={
								// deal_estimation_headerが存在する時はハイライトを見せる
								dealEstimationHistoryContext.dealConstructions.length > 0 ||
								requestHeader !== null
							}
						/>
						<Box mt={16}>
							<Panel>
								<PanelHeader
									description={
										<>
											デフォルトの内容は
											<TextLink
												color="link"
												href={ACCOUNT_PATH}
												target="_blank"
											>
												こちら
											</TextLink>
											で設定できます。
										</>
									}
									rightArea={
										<Button
											disabled={constructionAddDisabled}
											onClick={() => {
												setIsUpdateNoteModalOpen(true);
											}}
											variant="secondary"
										>
											編集
										</Button>
									}
									title="備考"
								/>
								<PanelBody>
									<Alertv2 mb={designTokens["spacing/m"]} variant="info">
										<Paragraph>
											入力された備考は見積書に出力されます。
										</Paragraph>
									</Alertv2>
									<div
										className={
											dealEstimationHistoryContext.noteModified ||
											requestHeader?.noteModified
												? Styles.ModifiedNote
												: undefined
										}
									>
										<Accordion defaultOpen>{estimationNote()}</Accordion>
									</div>
								</PanelBody>
							</Panel>
						</Box>
						{deal.statusId === IN_ESTIMATION ? (
							<div className={Styles.CompleteButtonArea}>
								{isSubVendor(deal, vendorCompanyId) ? (
									<Button
										onClick={handleOpenConfirm}
										variant="primary"
										width={isSp ? "fill" : "default"}
									>
										完了して通知する
									</Button>
								) : (
									<Button
										onClick={handleSubmitComplete}
										variant="primary"
										width={isSp ? "fill" : "default"}
									>
										完了して戻る
									</Button>
								)}
							</div>
						) : (
							<>
								{dealEstimationHistoryContext && showRequestFooter ? (
									<ConstructionRequestFooter
										companyType="vendor"
										deal={deal}
										dealEstimationHistoryContext={dealEstimationHistoryContext}
										handleMutate={handleMutateEstimationHistories}
										handleMutateRequestHeader={handleMutateRequestHeader}
										handleUpdateCancelStatus={handleUpdateCancelStatus}
										handleUpdateRequestStatus={handleUpdateRequestStatus}
										isAdmin={session.admin}
										requestHeader={requestHeader}
									/>
								) : null}
							</>
						)}
						<ConfirmModal
							actionText="完了"
							confirmType="save"
							handleAccept={handleSubmitComplete}
							handleCancel={handleCloseConfirm}
							isOpen={showConfirm}
						>
							完了してよろしいですか？
						</ConfirmModal>
					</div>
				</div>
				{showAddModal ? (
					<ConstructionAddModal
						dealConstructionItems={dealConstructionItems}
						disabled={constructionAddDisabled}
						handleClose={(): void => {
							setShowAddModal(false);
						}}
						handleCreate={handleCreate}
						mutateConstructions={handleMutateEstimationHistories}
					/>
				) : null}
				{isUpdateNoteModalOpen ? (
					<NoteEditModal
						deal={deal}
						defaultNote={estimationNote()}
						onClose={() => {
							setIsUpdateNoteModalOpen(false);
						}}
					/>
				) : null}
			</VendorDealLayout>
		</VendorLayoutV2>
	);
};
